import React, { useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import { reading } from '../domain/reading';
import ConfirmDialog from '../components/ConfirmDialog';
import MileageDrawer from '../components/mileage/MileageDrawer';
import MileageOverview from '../components/mileage/MileageOverview';
import MileageTable from '../components/mileage/MileageTable';
import MileageChoiceDialog from '../components/mileage/MileageChoiceDialog';
import useMileageForm from '../hooks/useMileageForm';
import useMileageExpenseSettings from '../hooks/useMileageExpenseSettings';
import useMileageTopUp from '../hooks/useMileageTopUp';
import { todayISO } from '../domain/clock';
import { useMileage } from '../data/useMileage';
import { useLocale } from '../context/LocaleContext';
import { FOLLOWING, THIS_ENTRY, mileageMessage as m } from '../domain/mileage';
import s from '../components/mileage/Mileage.module.css';

// The Mileage Log (#476), MMS's sidebar page of the same name.
//
// Assembly only: the rules are core/src/mileage.rs, the drawer's state is
// useMileageForm, the reads and writes are useMileage. The empty state's
// words, the button's and the drawer's are MMS's.
//
// An overview, then a page a month (#521), as MMS's Mileage Log: Recent Trips
// cards, the year's distance, and a table a row per month (`MileageOverview`);
// a month opens "Trips in October 2026" (`MileageTable`), where each trip's ⋮
// has Edit and Delete. Which month is open is this page's state rather than a
// route, as Business Settings' tabs are.
//
// Edit and Delete act on one date (#554). A row is a trip on a date; for a
// date of a series, Save asks "Update Recurring Entries" and Delete asks
// whether to delete only that entry or it and every one after, as MMS does.
//
// The unit a new trip is logged in is the studio's, from its Region — the
// setting that decides the currency and the date format.
//
// A save makes expenses beside the trip when Business Settings' Mileage Log
// tab says to (#520): `expense` is that tab, read where the save is made.
export default function MileagePage() {
  const {
    trips, loading, failed, addTrip, updateTrip, removeTrip,
  } = useMileage();
  const { distanceUnit } = useLocale();
  const expense = useMileageExpenseSettings();
  const drawer = useMileageForm({ addTrip, updateTrip, unit: distanceUnit, expense });
  // The expenses a recurring trip is owed and has not been given (#554).
  useMileageTopUp();
  const today = todayISO();
  const [year, setYear] = useState(() => today.slice(0, 4));
  const [month, setMonth] = useState(null);
  // `{ trip, date, series }` while Delete asks.
  const [deleting, setDeleting] = useState(null);

  // The two questions' radios, MMS's words, built once the core is loaded.
  const updateChoices = useMemo(() => [[THIS_ENTRY, m('thisEntryOnly')], [FOLLOWING, m('thisAndFollowing')]], []);
  const deleteChoices = useMemo(() => [[THIS_ENTRY, m('deleteThisOnly')], [FOLLOWING, m('deleteFollowing')]], []);

  const remove = async (scope) => {
    const d = deleting;
    setDeleting(null);
    await removeTrip(d.trip, d.date, scope);
  };

  return (
    <AppShell title={m('pageTitle')}>
      <div className={s.page}>
        {(!loading || failed) && trips.length === 0 ? (
          <div className={s.panel}>
            <EmptyState
              reading={reading(!loading, trips.length, failed)}
              title={m('emptyTitle')}
              description={m('emptyDescription')}
              actionLabel={m('addMileage')}
              onAction={drawer.openNew}
            />
          </div>
        ) : null}
        {trips.length && !month ? (
          <MileageOverview
            trips={trips}
            unit={distanceUnit}
            today={today}
            year={year}
            onYear={setYear}
            onMonth={setMonth}
            onAdd={drawer.openNew}
            onRepeat={drawer.openRepeat}
          />
        ) : null}
        {trips.length && month ? (
          <MileageTable
            trips={trips}
            unit={distanceUnit}
            today={today}
            month={month}
            onBack={() => setMonth(null)}
            onAdd={drawer.openNew}
            onEdit={(row) => drawer.openEdit(row.trip, row.occurredOn)}
            onDelete={(row) => setDeleting({ trip: row.trip, date: row.occurredOn, series: row.series })}
          />
        ) : null}
      </div>

      <MileageDrawer
        open={drawer.open}
        editing={Boolean(drawer.editing)}
        form={drawer.form}
        errors={drawer.errors}
        canSave={drawer.canSave}
        saving={drawer.saving}
        autofilled={drawer.autofilled}
        onChange={drawer.patch}
        onClose={() => drawer.leave()}
        onCancel={() => drawer.leave()}
        onSave={() => drawer.save(false)}
        onSaveAndAdd={() => drawer.save(true)}
      />

      <ConfirmDialog
        open={Boolean(drawer.guard)}
        title="Unsaved Changes"
        message="Would you like to save or discard your changes before continuing?"
        confirmDisabled={!drawer.canSave}
        onConfirm={async () => {
          const next = drawer.guard;
          const ok = await drawer.save(false);
          drawer.setGuard(null);
          if (ok) next?.();
        }}
        onDiscard={() => { const next = drawer.guard; drawer.setGuard(null); next?.(); }}
        onCancel={() => drawer.setGuard(null)}
      />

      {/* Save of a changed date of a series (#554). */}
      <MileageChoiceDialog
        open={drawer.scopeQuestion}
        title={m('updateRecurringTitle')}
        choices={updateChoices}
        confirmLabel={m('update')}
        cancelLabel={m('cancel')}
        onConfirm={(scope) => drawer.answerScope(scope)}
        onCancel={drawer.cancelScope}
      />

      {/* Delete, in MMS's words (#554): a one-time trip is asked about, a
          date of a series asks which dates. */}
      <ConfirmDialog
        open={Boolean(deleting) && !deleting.series}
        title={m('deleteTitle')}
        message={m('deleteMessage')}
        discardLabel={m('cancel')}
        cancelLabel={null}
        confirmLabel={m('delete')}
        onConfirm={() => remove(THIS_ENTRY)}
        onDiscard={() => setDeleting(null)}
        onCancel={() => setDeleting(null)}
      />
      <MileageChoiceDialog
        open={Boolean(deleting?.series)}
        title={m('deleteTitle')}
        message={m('deleteRecurringMessage')}
        choices={deleteChoices}
        confirmLabel={m('delete')}
        cancelLabel={m('cancel')}
        onConfirm={remove}
        onCancel={() => setDeleting(null)}
      />
    </AppShell>
  );
}
